// /var/www/afra/web-design.rampagesoft.com/my-app/src/menu.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Navbar,
  Nav,
  Container,
  Offcanvas,
  NavDropdown,
} from "react-bootstrap";
import logoram from "./media/logo/LOGO_RAMPAGESOFT_2023.png";
import "./css/rampagesoft.css";
import "./css/menu.scss";

function ResponsiveNavbar() {
  const [show, setShow] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isFixed, setIsFixed] = useState(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  useEffect(() => {
    // Check if data is cached in sessionStorage
    const cachedData = sessionStorage.getItem("menuData");
    if (cachedData) {
      const parsedData = JSON.parse(cachedData);
      // Validate cached data structure
      if (
        parsedData?.contactInfo?.phone &&
        parsedData?.contactInfo?.line &&
        parsedData?.contactInfo?.phone2 &&
        parsedData?.contactInfo?.line2
      ) {
        setMenuData(parsedData);
        setLoading(false);
        return;
      } else {
        // Clear invalid cache
        sessionStorage.removeItem("menuData");
      }
    }

    // Fetch JSON with AbortController for cleanup
    const controller = new AbortController();
    fetch(`${process.env.PUBLIC_URL}/data/menuData.json`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        sessionStorage.setItem("menuData", JSON.stringify(data));
        setMenuData(data);
        setLoading(false);
      })
      .catch((fetchError) => {
        if (fetchError.name === "AbortError") return;
        console.error("Failed to fetch menu data:", fetchError);
        setError(fetchError.message);
        setLoading(false);
      });

    return () => controller.abort();
  }, []);

  // Handle scroll event to toggle fixed and sticky classes
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsFixed(true);
      } else {
        setIsFixed(false);
      }
      if (window.scrollY > 50) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Memoize menu items to prevent re-rendering
  const memoizedMenuItems = useMemo(() => {
    return menuData?.mainNav?.menuItems || [];
  }, [menuData]);

  // Render static navbar structure immediately
  return (
    <>
      {/* Top Header (progressive rendering) */}
      <div
        className="header-top d-none d-md-block"
        style={{ backgroundColor: "#9dccf1" }}
      >
        <div className="container">
          <div className="top-header__menu-wrapper d-flex justify-content-between align-items-center">
            {loading || error || !menuData ? (
              <ul className="header-top-menu d-flex">
                <li>
                  <span>Loading...</span>
                </li>
              </ul>
            ) : (
              <>
                <ul className="header-top-menu d-flex">
                  {menuData.topHeader.contactLinks.map((link) => (
                    <li key={link.id}>
                      <a className="link-menu-so" href={link.href}>
                        {link.text}
                      </a>
                    </li>
                  ))}
                </ul>
                <ul className="header-top-socail-menu d-flex align-items-center">
                  <a
                    href={menuData.topHeader.companyLink.href}
                    className="text-company-af mr-15"
                  >
                    {menuData.topHeader.companyLink.text}
                  </a>
                  {menuData.topHeader.socialLinks.map((link) => (
                    <li key={link.id}>
                      <a href={link.href} aria-label={link.ariaLabel}>
                        <i className={link.iconClass}></i>
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Navbar (renders immediately) */}
      <div className="mega__menu-wrapper">
        <div className="header__main">
          <Navbar
            bg="light"
            expand="lg"
            className={`bg-menu-rampage wd100 ${isFixed ? "fixed-nav" : ""} ${isSticky ? "sticky-nav" : ""}`}
            style={{ zIndex: 1050 }}
          >
            <Container className="header__main">
              <div className="header__left">
                <Navbar.Brand href={menuData?.mainNav?.brandHref || "/"}>
                  <img
                    src={logoram}
                    alt={menuData?.logo?.alt || "Logo"}
                  />
                </Navbar.Brand>
              </div>

              <Navbar.Toggle
                aria-controls="basic-navbar-nav"
                onClick={handleShow}
              />
              <Navbar.Offcanvas
                id="basic-navbar-nav"
                placement="end"
                show={show}
                onHide={handleClose}
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title>
                    <img
                      className="logomobile"
                      src={logoram}
                      alt={menuData?.offcanvasTitle?.altText || "RAMPAGESOFT LOGO"}
                    />
                  </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                  <div className="header__middle">
                    <Nav className="ms-auto">
                      {loading || error || !menuData ? (
                        <Nav.Link href="#">Loading...</Nav.Link>
                      ) : (
                        memoizedMenuItems.map((item) =>
                          item.submenu ? (
                            <NavDropdown
                              title={item.text}
                              id={`nav-dropdown-${item.id}`}
                              key={item.id}
                            >
                              {item.submenu.map((subItem) => (
                                <NavDropdown.Item
                                  href={subItem.href}
                                  key={subItem.id}
                                >
                                  {subItem.text}
                                </NavDropdown.Item>
                              ))}
                            </NavDropdown>
                          ) : (
                            <Nav.Link href={item.href} key={item.id}>
                              {item.text}
                            </Nav.Link>
                          )
                        )
                      )}
                    </Nav>
                  </div>

                  {loading || error || !menuData ? (
                    <div className="header__btn d-flex align-items-center justify-content-center dis-none-mo mar-15">
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <>
                      <div className="header__btn d-flex align-items-center justify-content-center dis-none-mo mar-15">
                        <span className="icon">
                          <i className={menuData.contactInfo.phone.iconClass}></i>
                        </span>
                        <div className="text">
                          <span className="text-bk">
                            {menuData.contactInfo.phone.label}
                          </span>
                          <a href={menuData.contactInfo.phone.href}>
                            <h5>{menuData.contactInfo.phone.number}</h5>
                          </a>
                        </div>
                      </div>

                      {menuData.contactInfo.phone2 && (
                        <div className="header__btn d-flex align-items-center justify-content-center dis-none-mo mar-15">
                          <span className="icon">
                            <i className={menuData.contactInfo.phone2.iconClass}></i>
                          </span>
                          <div className="text">
                            <span className="text-bk">
                              {menuData.contactInfo.phone2.label}
                            </span>
                            <a href={menuData.contactInfo.phone2.href}>
                              <h5>{menuData.contactInfo.phone2.number}</h5>
                            </a>
                          </div>
                        </div>
                      )}

                      <div className="header__btn d-flex align-items-center justify-content-center dis-none-mo mar-15">
                        <span className="icon">
                          <i className={menuData.contactInfo.line.iconClass}></i>
                        </span>
                        <div className="text">
                          <span>{menuData.contactInfo.line.label}</span>
                          <a href={menuData.contactInfo.line.href}>
                            <h5>{menuData.contactInfo.line.idText}</h5>
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                </Offcanvas.Body>
              </Navbar.Offcanvas>

              <div className="header__right dis-res-nav">
                <div className="header__action d-flex align-items-center">
                  <div className="header__btn-wrap d-none d-sm-inline-flex">
                    {loading || error || !menuData ? (
                      <div className="header__btn d-flex align-items-center justify-content-center">
                        <span>Loading...</span>
                      </div>
                    ) : (
                      <>
                        <div className="header__btn d-flex align-items-center justify-content-center">
                          <span className="icon">
                            <i className={menuData.contactInfo.phone.iconClass}></i>
                          </span>
                          <div className="text">
                            <a href={menuData.contactInfo.phone.href} className="phone-link">
                              <h5>{menuData.contactInfo.phone.number}</h5>
                            </a>
                            <hr className="phone-divider" />
                            <a href={menuData.contactInfo.phone2.href} className="phone-link">
                              <h5>{menuData.contactInfo.phone2.number}</h5>
                            </a>
                          </div>
                        </div>
                        <div className="header__btn d-flex align-items-center justify-content-center">
                          <span className="icon">
                            <i className={menuData.contactInfo.line.iconClass}></i>
                          </span>
                          <div className="text">
                            <a href={menuData.contactInfo.line.href}>
                              <h5>{menuData.contactInfo.line.idText}</h5>
                            </a>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Container>
          </Navbar>
        </div>
      </div>
    </>
  );
}

export default ResponsiveNavbar;